/**
 * components/clients/MergeHistoryEntry.tsx
 *
 * Renders a merge event in the ClientHistoryTab timeline.
 * Shows the merged-in client name, a diff of modified fields,
 * and an UndoMergeButton if still within the 30-day window.
 * Created Apr 2026.
 */

import { GitMerge } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import UndoMergeButton from './UndoMergeButton';
import type { MergeHistoryEntry as MergeHistoryEntryType } from '../../types/merge';

const FIELD_LABELS: Record<string, string> = {
  first_name: 'Prénom',
  last_name: 'Nom',
  email: 'Email',
  phone: 'Téléphone',
  notes: 'Notes',
};

interface MergeHistoryEntryProps {
  entry: MergeHistoryEntryType;
  currentClientId: string;
  onUndo: (mergeId: string) => Promise<boolean>;
  undoing: boolean;
}

export default function MergeHistoryEntryComponent({
  entry,
  currentClientId,
  onUndo,
  undoing,
}: MergeHistoryEntryProps) {
  const isTarget = entry.target_client_id === currentClientId;
  const snapshot = isTarget ? entry.source_snapshot : entry.target_client_snapshot_before;
  const mergedName =
    [snapshot?.last_name, snapshot?.first_name].filter(Boolean).join(' ') ||
    snapshot?.email ||
    'Fiche supprimée';

  return (
    <li className="relative pl-8 pb-6 last:pb-0">
      <div className="absolute left-0 top-0.5">
        <div className="w-7 h-7 rounded-full bg-neutral-100 border-2 border-neutral-300 flex items-center justify-center">
          <GitMerge className="w-3.5 h-3.5 text-neutral-500" />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <p className="text-sm font-medium text-neutral-900">
          {isTarget ? `Fusion avec ${mergedName}` : `Fiche fusionnée dans une autre fiche`}
        </p>
        <p className="text-xs text-neutral-400">{formatDate(entry.merged_at)}</p>

        {isTarget && entry.field_changes.length > 0 && (
          <div className="mt-1.5 space-y-1">
            {entry.field_changes.map((change) => (
              <div key={change.field} className="flex items-baseline gap-1.5 text-xs text-neutral-600">
                <span className="font-medium text-neutral-500 w-20 flex-shrink-0">
                  {FIELD_LABELS[change.field] ?? change.field}
                </span>
                <span className="line-through text-neutral-400 truncate max-w-[120px]">
                  {change.old_value || '-'}
                </span>
                <span className="text-neutral-300">→</span>
                <span className="text-neutral-700 truncate max-w-[120px]">
                  {change.new_value || '-'}
                </span>
              </div>
            ))}
          </div>
        )}

        {isTarget && (
          <div className="mt-2">
            <UndoMergeButton
              mergeId={entry.id}
              canUndoUntil={entry.can_undo_until}
              onUndo={onUndo}
              undoing={undoing}
            />
          </div>
        )}
      </div>
    </li>
  );
}
